/**
 * Handles the add-to-cart operation.
 */
if (!customElements.get("product-form")) {
  customElements.define(
    "product-form",
    class ProductForm extends HTMLElement {
      constructor() {
        super();
        this.form = this.querySelector("form");
        if (!this.form) return;

        this.isloading = false;
        this.error = false;
        if (this.variantIdInput) this.variantIdInput.disabled = false;
        this.form.addEventListener("submit", this.onSubmitHandler.bind(this)); // Add event listener for form submission

        // Bundle Sale form initialization
        this.bundleSale = document.getElementById(
          `Bundle-Sale-${this.dataset.section}`,
        );
        // Care Service
        this.CareService = document.getElementById(
          `Care-Service-${this.dataset.section}`,
        );
        // Listen for changes in the main product quantity and synchronize the quantity with bundled products
        if (this.bundleSale) {
          const QuantityInput = document.getElementById(
            `Quantity-${this.dataset.section}`,
          );
          if (QuantityInput)
            QuantityInput.addEventListener("change", (event) => {
              this.bundleSale.setItemsQuantity(event.target.value);
            });
        }

        this.addCartButton = this.querySelector('button[type="submit"]'); // 添加购物车按钮
        this.miniAddCartButton = document.getElementById(
          `Mini-Submit-Button-${this.dataset.section}`,
        ); // 迷你结账按钮

        // 设置添加购物车按钮相关的辅助设备属性
        this.cartDrawer = document.getElementById("Cart-Drawer");
        if (
          this.cartDrawer &&
          !this.cartDrawer.hasAttribute("data-status-silence")
        ) {
          if (this.addCartButton) {
            this.addCartButton.setAttribute("aria-haspopup", "dialog");
            this.addCartButton.setAttribute("aria-expanded", "false");
            this.addCartButton.setAttribute("aria-controls", "Cart-Drawer");
          }

          if (this.miniAddCartButton) {
            this.miniAddCartButton.setAttribute("aria-haspopup", "dialog");
            this.miniAddCartButton.setAttribute("aria-expanded", "false");
            this.miniAddCartButton.setAttribute("aria-controls", "Cart-Drawer");
          }
        }

        // 错误消失提示容器
        this.errorMessageWrapper = document.getElementById(
          `Product-Form-Error-Message-${this.dataset.section}`,
        );

        // 如果是在快速预览抽屉内
        // 只有快速预览抽屉会给其添加data-original-section属性
        if (this.hasAttribute("data-original-section")) {
          this.quickAddDrawer = document.getElementById(
            "Product-Quick-View-Drawer",
          );
        }

        // 迷你结账相关
        this.miniChecker = document.getElementById(
          `Mini-Check-${this.dataset.section}`,
        );
        if (this.miniChecker) {
          let isMiniCheckShouldHide = true; // 是否应该隐藏迷你结账
          // 如果迷你结账存在，添加相关事件监听
          this.productFormObserver = new IntersectionObserver(
            (entries, observer) => {
              const rootBoundsTop = entries[0].rootBounds?.top || 0; // 防止iframe中（模板编辑器中）无法获取 rootBounds 属性
              if (entries[0].isIntersecting) {
                isMiniCheckShouldHide = true;
                this.hideMiniCheck();
              } else if (entries[0].boundingClientRect.top < rootBoundsTop) {
                isMiniCheckShouldHide = false;
                this.showMiniCheck();
              }
            },
            {
              root: null,
              rootMargin: "-200px 0px 200px 0px",
            },
          );
          this.productFormObserver.observe(this);

          // 监听页脚显示，出现的话需要隐藏迷你结账，防止遮挡
          const footer = document.getElementById("Page-Footer");
          if (footer) {
            this.footerObserver = new IntersectionObserver(
              (entries) => {
                if (entries[0].isIntersecting) {
                  this.hideMiniCheck();
                } else {
                  if (!isMiniCheckShouldHide) this.showMiniCheck();
                }
              },
              {
                root: null,
                rootMargin: "0px 0px 0px 0px",
              },
            );

            // 监听页脚元素
            this.footerObserver.observe(footer);
          }

          // 同步数量
          const quantityInput = document.getElementById(
            `Quantity-${this.dataset.section}`,
          );
          if (quantityInput)
            quantityInput.addEventListener("change", (event) => {
              const miniQuantityInput = document.getElementById(
                `Mini-Quantity-${this.dataset.section}`,
              );
              if (miniQuantityInput) {
                // 同步数量
                miniQuantityInput.value = event.target.value;

                // 触发目标元素的change事件，不许冒泡
                miniQuantityInput.dispatchEvent(
                  new Event("change", { bubbles: false }),
                );
              }
            });
        }
      }

      disconnectedCallback() {
        if (this.productFormObserver) this.productFormObserver.disconnect();
        if (this.footerObserver) this.footerObserver.disconnect();
      }

      get variantIdInput() {
        return this.form.querySelector("[name=id]");
      }

      /**
       * 是否隐藏错误信息
       * 当产品是礼品卡的时候，隐藏错误信息
       * @returns {boolean}
       */
      get hideErrors() {
        return this.dataset.hideErrors === "true";
      }

      /**
       * 处理点击提交购物车
       * @param event
       */
      async onSubmitHandler(event) {
        if (this.isloading) return;
        
        // CRITICAL: Check if global AJAX interceptor of pre-cart.js already handled this
        // Check this FIRST before doing anything else
        if (event.defaultPrevented || 
            window._precartRedirecting || 
            event.cancelBubble ||
            (this.form && this.form.dataset.ajaxProcessing === 'true')) {
          // Event already handled - don't process again
          return;
        }
        
        event.preventDefault();

        if (this.addCartButton.hasAttribute("disabled")) return;

        this.handleErrorMessage();
        this.startLoading();

        const config = webvista.fetchConfig("javascript");
        config.headers["X-Requested-With"] = "XMLHttpRequest";
        delete config.headers[
          "Content-Type"
        ]; /* 浏览器会自动为 formData 对象生成带有边界(boundary)的 multipart/form-data 类型*/

        // 获取表单值
        const formData = new FormData(this.form); // 原表单数据

        // 判断是否有捆绑销售
        if (this.bundleSale) {
          const bundleItems = this.bundleSale.getSelectedItems(
            formData.get("id"),
          ); // 获取所有已选的捆绑产品

          if (bundleItems.length > 0)
            bundleItems.forEach((item, index) => {
              formData.append(`items[][id]`, item["id"]);
              formData.append(`items[][quantity]`, item["quantity"]);
              formData.append(`items[][parent_id]`, item["parent_id"]);
            });
        }

        // 判断是否有Care服务
        if (this.CareService) {
          const careId = this.CareService.getSelectedId();
          if (careId) {
            formData.append(`items[][id]`, careId);
            formData.append(`items[][quantity]`, formData.get("quantity"));
            formData.append(`items[][parent_id]`, formData.get("id"));
          }
        }

        // Check voor geselecteerde accessoires (product pagina)
        if (window.selectedProductAccessoires && typeof window.selectedProductAccessoires.getSelected === 'function') {
          const selectedAccessoires = window.selectedProductAccessoires.getSelected();
          
          // Valideer inventory voor alle geselecteerde accessoires
          if (selectedAccessoires.length > 0) {
            try {
              const isValid = await window.selectedProductAccessoires.validateAll();
              if (!isValid) {
                this.stopLoading();
                this.error = true;
                return; // Stop execution if validation failed
              }
            } catch (error) {
              console.error('Error validating accessoires:', error);
              // Continue on validation error - don't block user
            }

            // Voeg accessoires toe aan formData met hun eigen quantity
            selectedAccessoires.forEach((accessoire) => {
              // Support both old format (just variantId string) and new format (object with variantId and quantity)
              const variantId = typeof accessoire === 'string' ? accessoire : accessoire.variantId;
              const quantity = typeof accessoire === 'string' ? (formData.get("quantity") || "1") : (accessoire.quantity || "1");
              
              formData.append(`items[][id]`, variantId);
              formData.append(`items[][quantity]`, quantity.toString());
            });
          }
        }

        formData.append(
          "sections",
          this.getSectionsToRender()
            .map((section) => section.section)
            .join(),
        );
        formData.append("sections_url", window.location.pathname);
        config.body = formData;

        // Check inventory before adding (ALLEEN als inventory tracking aan staat)
        // Dit gebeurt alleen bij form submit (click op add to cart), niet preventief
        const variantId = formData.get("id");
        const variantInput = this.form.querySelector(`input[name="id"][value="${variantId}"]`);
        
        // Alleen checken als inventory management expliciet aan staat
        if (variantInput && variantInput.dataset.inventoryManagement === 'true') {
          const inventoryQuantity = variantInput.dataset.inventoryQuantity;
          // Alleen checken als inventory quantity beschikbaar is (niet 'null' of leeg)
          if (inventoryQuantity && inventoryQuantity !== 'null' && inventoryQuantity !== null) {
            const maxQuantity = parseInt(inventoryQuantity, 10);
            // Alleen checken als maxQuantity een geldig getal is en groter dan 0
            if (!isNaN(maxQuantity) && maxQuantity > 0) {
              // Fetch cart ONLY bij form submit (niet preventief)
              try {
                const cartResponse = await fetch('/cart.js');
                const cart = await cartResponse.json();
                const currentItem = cart.items.find(item => item.variant_id.toString() === variantId.toString());
                const currentQuantity = currentItem ? currentItem.quantity : 0;
                const requestedQuantity = parseInt(formData.get("quantity") || "1", 10);
                const newTotal = currentQuantity + requestedQuantity;
                
                // Alleen blokkeren als inventory_policy 'deny' is (geen backorder)
                if (newTotal > maxQuantity && variantInput.dataset.inventoryPolicy === 'deny') {
                  // Vertaalbare error message
                  const maxQtyMessage = window.cartStrings?.maxQuantityReached 
                    ? window.cartStrings.maxQuantityReached.replace('[quantity]', maxQuantity)
                    : `Maximaal ${maxQuantity} beschikbaar`;
                  
                  // Show error feedback op button
                  this.showErrorFeedback();
                  
                  // Error toast verwijderd - inline error message is voldoende
                  this.handleErrorMessage(maxQtyMessage);
                  this.endLoading();
                  this.error = true;
                  return;
                }
              } catch (error) {
                console.error('Error checking inventory:', error);
                // Continue on error - don't block user if inventory check fails
              }
            }
          }
        }

        fetch(`${window["routes"]["cart_add_url"]}`, config)
          .then((response) => {
            return response.json();
          })
          .then((response) => {
            if (response.status) {
              // Check if this is a partial add (items were added but less than requested)
              const description = response.description || '';
              const isPartialAdd = description.toLowerCase().includes('was added') || 
                                   description.toLowerCase().includes('were added') ||
                                   description.includes('toegevoegd vanwege');
              
              if (isPartialAdd) {
                // Partial add: items WERE added, so update cart but show inline error only
                this.error = false; // Not a blocking error
                
                // Fetch fresh cart data and update UI (partial add response has no sections)
                fetch('/cart.js')
                  .then(r => r.json())
                  .then(cart => {
                    const count = cart.item_count || 0;
                    // Update all cart count elements
                    document.querySelectorAll('[data-cart-count], .cart-count-bubble span, #cart-icon-bubble span, .header__cart-count').forEach(el => {
                      el.textContent = count;
                    });
                    
                    // Also fetch and update cart drawer content
                    const sectionsToFetch = this.getSectionsToRender().map(s => s.section).join(',');
                    return fetch(`${window.location.pathname}?sections=${sectionsToFetch}`);
                  })
                  .then(r => r.json())
                  .then(sections => {
                    SectionDynamicUpdate.updateSections(this.getSectionsToRender(), sections);
                  })
                  .catch(err => console.warn('Cart refresh error:', err));
                
                // Publish cart update event
                webvista.publish(PUB_SUB_EVENTS.cartUpdate, {
                  source: "product-form",
                  productVariantId: formData.get("id"),
                });
                
                // Update cart badges via global function
                if (typeof window.updateCartBadges === 'function') {
                  window.updateCartBadges();
                }
                
                // Show inline error message ONLY (no push notification)
                // Use translated message if available
                const addedMatch = description.match(/Only (\d+)/i) || description.match(/Slechts (\d+)/i);
                const addedQty = addedMatch ? addedMatch[1] : '?';
                const translatedMessage = window.cartStrings && window.cartStrings.partialAddMessage 
                  ? window.cartStrings.partialAddMessage.replace('[quantity]', addedQty)
                  : description;
                this.handleErrorMessage(translatedMessage);
                
                // Open cart drawer if available
                if (this.cartDrawer && !this.cartDrawer.hasAttribute("data-status-silence")) {
                  this.cartDrawer.show(event.submitter);
                }
                
                return; // Don't treat as full error
              }
              
              // Full error (nothing was added)
              webvista.publish(PUB_SUB_EVENTS.cartError, {
                source: "product-form",
                productVariantId: formData.get("id"),
                errors: response.errors || response.description,
                message: response.message,
              });

              // Show error feedback op button
              this.showErrorFeedback();

              // Check of dit een maximum quantity error is (geen toast voor deze)
              // Note: 'description' is already declared at line 309, reuse it
              const isMaxQuantityError = description.toLowerCase().includes('maximum quantity') ||
                                        description.toLowerCase().includes('maximaal') ||
                                        description.toLowerCase().includes('max quantity') ||
                                        description.toLowerCase().includes('already in your cart');

              // Show error toast ONLY for full errors (nothing added) EN niet voor maximum quantity
              // Maximum quantity errors hebben al een inline error message
              if (!isMaxQuantityError) {
                const fullErrorMessage = description || 
                                         window.cartStrings?.couldNotAdd || 
                                         'Er is een fout opgetreden bij het toevoegen aan de winkelwagen';
                if (window.CartUtils && typeof window.CartUtils.showCartErrorToast === 'function') {
                  window.CartUtils.showCartErrorToast(fullErrorMessage);
                } else if (typeof window.showCartErrorToast === 'function') {
                  window.showCartErrorToast(fullErrorMessage);
                }
              }

              this.handleErrorMessage(response.description);
              return (this.error = true);
            }
            this.error = false;

            // 动态更新Sections内容
            SectionDynamicUpdate.updateSections(
              this.getSectionsToRender(),
              response["sections"],
            );

            // 发布购物车更新事件
            webvista.publish(PUB_SUB_EVENTS.cartUpdate, {
              source: "product-form",
              productVariantId: formData.get("id"),
            });

            // Check if accessoires were selected via new product-page-accessoires system
            const hasSelectedAccessoires = window.selectedProductAccessoires && 
                                          typeof window.selectedProductAccessoires.getSelected === 'function' &&
                                          window.selectedProductAccessoires.getSelected().length > 0;

            // Update cart badges - gebruik CartUtils indien beschikbaar
            if (window.CartUtils && typeof window.CartUtils.updateCartBadges === 'function') {
              window.CartUtils.updateCartBadges();
            } else if (typeof window.updateCartBadges === 'function') {
              window.updateCartBadges();
            }

            // Show success feedback op button
            this.showSuccessFeedback();

            // Show checkmark feedback voor buy button (zowel met als zonder accessoires)
            const submitButton = event.submitter || this.addCartButton;
            if (submitButton && typeof window.showBuyButtonCartCheckmark === 'function') {
              const hasStickyIcon = submitButton.querySelector('.kc-sticky-icon');
              if (hasStickyIcon) {
                window.showBuyButtonCartCheckmark(submitButton);
              }
            }

            // Success toast verwijderd - feedback gebeurt via button animaties

            // If accessoires were selected, show checkmark in header cart icon and stay on page
            if (hasSelectedAccessoires) {
              // Show checkmark feedback in header cart icon
              if (typeof window.showHeaderCartCheckmark === 'function') {
                window.showHeaderCartCheckmark();
              }
              
              // Don't redirect - stay on product page
              return;
            }

            // Original logic: Show checkmark feedback on button for product cards (collection pages)
            if (submitButton && typeof window.showAddToCartFeedback === 'function') {
              const cartIcon = submitButton.querySelector('.kc-cart-icon');
              if (cartIcon) {
                window.showAddToCartFeedback(submitButton);
              }
            }

            // Check of dit product accessoires heeft
            const productFormElement = this.closest('product-form') || this;
            const hasAccessoires = (this.form && this.form.dataset && this.form.dataset.hasAccessoires === 'true') ||
                                 (productFormElement && productFormElement.dataset && productFormElement.dataset.hasAccessoires === 'true');

            if (hasAccessoires && typeof window.KCShowAccessoiresAddedPopup === 'function') {
              const productHandle = productFormElement.dataset.productHandle || '';
              const productUrl = productFormElement.dataset.productUrl || '/products/' + productHandle;

              fetch(productUrl + '?sections=kc-collapsible-content')
                .then(res => res.json())
                .then(sections => {
                  const html = sections['kc-collapsible-content'] || '';
                  const parser = new DOMParser();
                  const doc = parser.parseFromString(html, 'text/html');
                  const accessoiresGrid = doc.querySelector('[data-accessoires-section]');
                  return accessoiresGrid ? accessoiresGrid.innerHTML : '';
                })
                .then(accessoiresHtml => {
                  window.KCShowAccessoiresAddedPopup({
                    variantId: formData.get('id'),
                    title: productFormElement.dataset.productTitle || '',
                    image: productFormElement.dataset.productImage || '',
                    price: productFormElement.dataset.productPrice || '',
                    url: productUrl,
                    accessoiresHtml: accessoiresHtml
                  });
                })
                .catch(err => {
                  console.warn('Could not load accessoires for popup:', err);
                });
              return;
            }
          })
          .catch((error) => {
            const errorMessage = window.cartStrings?.couldNotAdd || 
                                window["accessibilityStrings"]?.["unknownError"] || 
                                'Er is een fout opgetreden bij het toevoegen aan de winkelwagen';
            
            // Show error feedback op button
            this.showErrorFeedback();
            
            // Show error toast - alleen als geen inline error getoond wordt
            if (window.CartUtils && typeof window.CartUtils.showCartErrorToast === 'function') {
              window.CartUtils.showCartErrorToast(errorMessage);
            } else if (typeof window.showCartErrorToast === 'function') {
              window.showCartErrorToast(errorMessage);
            }
            
            this.handleErrorMessage(errorMessage);
            this.error = true;
          })
          .finally(() => {
            this.endLoading();
          });
      }

      /**
       * 获取需要重新渲染的位置
       * @returns {[{section: string, selector: string, id: string}]}
       */
      getSectionsToRender() {
        const sections = [
          {
            id: "Cart-Icon-Bubble",
            section: "cart-icon-bubble",
            selector: ".shopify-section",
          },
          {
            id: "Bottom-Cart-Bubble",
            section: "cart-icon-bubble",
            selector: ".shopify-section",
          },
          {
            id: "Cart-Drawer",
            section: "cart-drawer",
            selector: ".cart-count",
          },
        ];

        if (
          this.cartDrawer &&
          !this.cartDrawer.hasAttribute("data-status-silence")
        ) {
          sections.push({
            id: "Cart-Drawer",
            section: this.cartDrawer.dataset.section,
            selector: "#Cart-Drawer-Details",
          });
        }

        return sections;
      }

      /**
       * 错误显示
       * @param errorMessage
       */
      handleErrorMessage(errorMessage = null) {
        if (this.hideErrors || !this.errorMessageWrapper) return;

        this.errorMessageWrapper.toggleAttribute("hidden", !errorMessage);
        this.errorMessageWrapper.textContent = errorMessage ? errorMessage : "";
      }

      startLoading() {
        this.isloading = true;

        // Gebruik CartUtils indien beschikbaar, anders fallback
        if (window.CartUtils && typeof window.CartUtils.startLoading === 'function') {
          window.CartUtils.startLoading(this.addCartButton);
          if (this.miniAddCartButton) {
            window.CartUtils.startLoading(this.miniAddCartButton);
          }
        } else {
          // Fallback naar oude methode
          this.addCartButton.setAttribute("disabled", "disabled");
          this.addCartButton.classList.add("loading", "is-loading");
          if (this.miniAddCartButton) {
            this.miniAddCartButton.setAttribute("disabled", "disabled");
            this.miniAddCartButton.classList.add("loading", "is-loading");
          }
        }
      }

      endLoading() {
        this.isloading = false;

        // Gebruik CartUtils indien beschikbaar
        if (window.CartUtils && typeof window.CartUtils.stopLoading === 'function') {
          window.CartUtils.stopLoading(this.addCartButton);
          if (this.miniAddCartButton) {
            window.CartUtils.stopLoading(this.miniAddCartButton);
          }
        } else {
          // Fallback
          this.addCartButton.removeAttribute("disabled");
          this.addCartButton.classList.remove("loading", "is-loading");
          if (this.miniAddCartButton) {
            this.miniAddCartButton.removeAttribute("disabled");
            this.miniAddCartButton.classList.remove("loading", "is-loading");
          }
        }

        this.addCartButton.removeAttribute("disabled");
        this.addCartButton.classList.remove("loading", "is-loading");
        if (this.miniAddCartButton) {
          this.miniAddCartButton.removeAttribute("disabled");
          this.miniAddCartButton.classList.remove("loading", "is-loading");
        }

        // 关闭快速预览
        if (!this.error && this.quickAddDrawer) {
          this.quickAddDrawer.hide();
        }
      }

      /**
       * Toon success feedback op button
       */
      showSuccessFeedback() {
        if (window.CartUtils && typeof window.CartUtils.showSuccess === 'function') {
          window.CartUtils.showSuccess(this.addCartButton);
          if (this.miniAddCartButton) {
            window.CartUtils.showSuccess(this.miniAddCartButton);
          }
        } else {
          // Fallback: voeg is-success class toe
          this.addCartButton.classList.add("is-success");
          if (this.miniAddCartButton) {
            this.miniAddCartButton.classList.add("is-success");
          }
          setTimeout(() => {
            this.addCartButton.classList.remove("is-success");
            if (this.miniAddCartButton) {
              this.miniAddCartButton.classList.remove("is-success");
            }
          }, 1500);
        }
      }

      /**
       * Toon error feedback op button
       */
      showErrorFeedback() {
        if (window.CartUtils && typeof window.CartUtils.showError === 'function') {
          window.CartUtils.showError(this.addCartButton);
          if (this.miniAddCartButton) {
            window.CartUtils.showError(this.miniAddCartButton);
          }
        } else {
          // Fallback: shake animatie
          this.addCartButton.classList.add("add-to-cart-button-shake");
          setTimeout(() => {
            this.addCartButton.classList.remove("add-to-cart-button-shake");
          }, 500);
        }
      }

      showMiniCheck() {
        if (this.miniChecker) this.miniChecker.classList.add("active");
      }

      hideMiniCheck() {
        if (this.miniChecker) this.miniChecker.classList.remove("active");
      }
    },
  );
}
